/*
 * The live run at the top of the page: a simulated prospect journey
 * (signal → target → conversation → meeting) and Adam's brain
 * updating itself as it goes. The timeline is plain DOM and runs
 * without WebGL; the globe listens to its events when available.
 */
import G from './gl';

export function initDemo() {
  const engine = document.querySelector(".engine");
  if (!engine) return;

  const $ = id => document.getElementById(id);

  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  const stateLabel = $("engineState");
  const substateLabel = $("engineSubstate");
  const thread = $("thread");
  const threadWith = $("threadWith");
  const brainLog = $("brainLog");
  const personaVersion = $("personaVersion");
  const brainVersionTag = $("brainVersionTag");
  const memoryCount = $("memoryCount");
  const brainLabel = document.querySelector(".label-right");
  const lockCaption = $("lockCaption");
  const journeySteps = [...document.querySelectorAll(".journey-step")];
  const journeyProgress = $("journeyProgress");
  const judges = [...document.querySelectorAll(".judge")];
  const traits = Object.fromEntries(
    [...document.querySelectorAll(".trait[data-trait]")].map(el => [el.dataset.trait, el])
  );

  const prospects = [
    {
      initials: "AM",
      name: "Alex Morgan",
      first: "Alex",
      role: "Founder · NovaTech",
      channel: "LinkedIn",
      score: 96,
      tags: ["SAAS", "SERIES A", "HIRING SDRS", "BUYING SIGNAL"],
      signal: {
        headline: "NovaTech closes €12M Series A",
        source: "techcrunch.com",
        state: "NOVATECH JUST CLOSED ITS SERIES A"
      },
      opener: "Hi Alex — <mark>congrats on closing the Series A.</mark> Teams going from 12 to 40 reps usually find pipeline can't keep up with headcount. Worth 20 minutes to see how we handle that?",
      reply: "Timing's interesting — we're hiring 3 SDRs right now. What does it actually do?",
      intent: ["“hiring 3 SDRs” → high intent", "seen 214×"],
      close: "Short version: it finds, qualifies and books — your SDRs only take the calls. Tuesday 10:30 or Thursday 14:00?",
      answer: "Tuesday works. Send the invite.",
      meeting: ["Discovery call · Alex Morgan", "Tuesday, 10:30 · 30 minutes"],
      lessons: [
        ["playbook", "Two-slot close converted", "weight +0.08"],
        ["persona", "Series A founders answer short openers faster", "2.4×"]
      ],
      traits: { brevity: 7, directness: 4, urgency: 3, warmth: -2 }
    },
    {
      initials: "PS",
      name: "Priya Shah",
      first: "Priya",
      role: "VP Sales · Lumen Logistics",
      channel: "Email",
      score: 93,
      tags: ["LOGISTICS", "EXPANSION", "NEW HUBS", "BUYING SIGNAL"],
      signal: {
        headline: "Lumen opens two hubs in Rotterdam",
        source: "logistics-news.eu",
        state: "LUMEN IS EXPANDING INTO ROTTERDAM"
      },
      opener: "Hi Priya — <mark>saw Lumen is opening two hubs in Rotterdam.</mark> New regions usually mean accounts to win before the trucks are even running. Open to a quick look at how we'd fill that pipeline?",
      reply: "Inbound is fine, honestly. Outbound is where we're stuck. How is this different from an agency?",
      intent: ["“outbound is where we're stuck” → pain confirmed", "objection: agency"],
      close: "An agency rents you their playbook. Adam builds yours — and you keep it. Wednesday 9:00 or Friday 11:30?",
      answer: "Friday 11:30. Loop in my head of SDRs.",
      meeting: ["Pipeline review · Priya Shah + 1", "Friday, 11:30 · 45 minutes"],
      lessons: [
        ["objection", "“Agency” objection → ownership reframe worked", "weight +0.11"],
        ["persona", "Logistics VPs bring a second attendee", "seen 37×"]
      ],
      traits: { warmth: 5, directness: 3, urgency: 2, brevity: -2 }
    }
  ];

  const brain = {
    version: 42,
    memories: 18406,
    traits: { directness: 62, brevity: 55, warmth: 48, urgency: 40 }
  };

  const listeners = [];
  const emit = type => listeners.forEach(listener => listener(type));

  const format = n => n.toLocaleString("en-US");

  function setState(label, sub) {
    stateLabel.textContent = label;
    substateLabel.textContent = sub;
  }

  function setJourney(index, finished = false) {
    journeySteps.forEach((step, i) => {
      step.classList.toggle("active", i === index && !finished);
      step.classList.toggle("done", i < index || (finished && i <= index));
    });
    journeyProgress.style.width = Math.max(0, index) / (journeySteps.length - 1) * 100 + "%";
  }

  function addMemories(count) {
    brain.memories += count;
    memoryCount.textContent = format(brain.memories);
    brainLabel.classList.add("bump");
    setTimeout(() => brainLabel.classList.remove("bump"), 700);
  }

  function log(tag, text, note) {
    const item = document.createElement("li");
    item.innerHTML = `<span class="log-tag ${tag}">${tag}</span><span>${text}${note ? ` <em>· ${note}</em>` : ""}</span>`;
    brainLog.prepend(item);
    while (brainLog.children.length > 8) brainLog.lastElementChild.remove();
    if (tag !== "boot") addMemories(1 + Math.floor(Math.random() * 3));
  }

  function removeTyping() {
    thread.querySelectorAll(".typing").forEach(el => el.remove());
  }

  function showTyping(side) {
    removeTyping();
    const el = document.createElement("div");
    el.className = `msg ${side} typing`;
    el.innerHTML = `<div class="bubble"><i></i><i></i><i></i></div>`;
    thread.append(el);
  }

  function say(side, html, meta) {
    removeTyping();
    const el = document.createElement("div");
    el.className = `msg ${side}`;
    el.innerHTML = `<div class="bubble">${html}</div><div class="msg-meta">${meta}</div>`;
    thread.append(el);
  }

  function runJudges(fast) {
    const step = fast ? 260 : 420;
    judges.forEach(judge => {
      judge.classList.remove("pass");
      judge.classList.add("checking");
      const score = judge.querySelector("b");
      if (score) score.textContent = "—";
    });
    judges.forEach((judge, i) => {
      later((i + 1) * step / 1000, () => {
        judge.classList.remove("checking");
        judge.classList.add("pass");
        const score = judge.querySelector("b");
        if (score) score.textContent = 95 + Math.floor(Math.random() * 4);
      });
    });
  }

  function learn(deltas) {
    Object.entries(deltas).forEach(([key, delta]) => {
      const el = traits[key];
      if (!el) return;
      brain.traits[key] = Math.max(8, Math.min(96, brain.traits[key] + delta));
      el.style.setProperty("--v", brain.traits[key] + "%");
      el.querySelector("b").textContent = brain.traits[key];
      const badge = el.querySelector("em");
      badge.textContent = (delta > 0 ? "+" : "") + delta;
      badge.classList.add("show");
      later(2.2, () => badge.classList.remove("show"));
    });
  }

  function renderTraits() {
    Object.entries(traits).forEach(([key, el]) => {
      el.style.setProperty("--v", brain.traits[key] + "%");
      el.querySelector("b").textContent = brain.traits[key];
    });
  }

  function bumpVersion() {
    brain.version++;
    personaVersion.textContent = "v" + brain.version;
    brainVersionTag.textContent = "v" + brain.version;
  }

  function fillTarget(p) {
    $("targetInitials").textContent = p.initials;
    $("targetName").textContent = p.name;
    $("targetRole").textContent = p.role;
    $("targetScore").textContent = p.score;
    $("targetTags").innerHTML = p.tags.map(tag => `<span>${tag}</span>`).join("");
    threadWith.textContent = `${p.channel} · ${p.name}`;
  }

  function book(p) {
    removeTyping();
    const el = document.createElement("div");
    el.className = "booked-card";
    el.innerHTML = `
      <div class="calendar-icon">▦</div>
      <div class="calendar-copy">
        <span class="mono">Meeting booked</span>
        <strong>${p.meeting[0]}</strong>
        <small>${p.meeting[1]}</small>
      </div>
      <div class="calendar-check">✓</div>`;
    thread.append(el);
  }

  /*
   * Timeline. Every step is scheduled relative to the start of a run.
   */
  let timers = [];
  let run = 0;

  function later(seconds, fn) {
    timers.push(setTimeout(fn, reducedMotion ? 0 : seconds * 1000));
  }

  function reset() {
    timers.forEach(clearTimeout);
    timers = [];
    thread.innerHTML = "";
    engine.classList.remove("target-found", "meeting-booked");
    lockCaption.textContent = "TARGET IDENTIFIED";
    judges.forEach(judge => judge.classList.remove("checking", "pass"));
    setJourney(-1);
    emit("reset");
  }

  function play() {
    reset();

    const p = prospects[run % prospects.length];
    run++;
    fillTarget(p);

    const script = [
      [0.3, () => {
        setState("MAPPING MARKET", "SCANNING 4,800 SOURCES · READING SIGNALS");
        log("boot", `Brain v${brain.version} loaded`, `${format(brain.memories)} memories`);
      }],
      [1.9, () => {
        setState("SIGNAL DETECTED", p.signal.state);
        log("signal", p.signal.headline, p.signal.source);
        emit("signal");
      }],
      [3.5, () => {
        setState("TARGET IDENTIFIED", `${p.name.toUpperCase()} · ${p.score}% FIT`);
        engine.classList.add("target-found");
        setJourney(0);
        log("match", `${p.name} · ${p.role}`, `${p.score}% fit`);
        emit("target");
      }],
      [4.9, () => {
        setState("DRAFTING", "4 JUDGES REVIEW EVERY MESSAGE");
        showTyping("out");
        runJudges(false);
      }],
      [7.2, () => {
        setState("CONTACTED", "PERSONAL · SIGNAL-AWARE · HUMAN");
        say("out", p.opener, `Adam · uses <b>${p.signal.source}</b> · 4/4 judges`);
        setJourney(1);
        log("judge", "Opener approved 4/4", "human-likeness 97");
        emit("send");
      }],
      [8.8, () => showTyping("in")],
      [10.4, () => {
        setState("CONVERSATION", "READING INTENT · UPDATING THE BRAIN");
        say("in", p.reply, p.first);
        setJourney(2);
        emit("reply");
      }],
      [11.2, () => {
        log("intent", ...p.intent);
        learn(p.traits);
        emit("learn");
      }],
      [12.1, () => {
        showTyping("out");
        runJudges(true);
      }],
      [13.8, () => {
        say("out", p.close, "Adam · 4/4 judges");
        emit("send");
      }],
      [15.2, () => showTyping("in")],
      [16.5, () => {
        say("in", p.answer, p.first);
        emit("reply");
      }],
      [17.3, () => {
        setState("MEETING BOOKED", p.meeting[1].toUpperCase());
        book(p);
        engine.classList.add("meeting-booked");
        lockCaption.textContent = "MEETING BOOKED";
        setJourney(3);
        log("book", p.meeting[0], "calendar synced");
        emit("booked");
      }],
      [18.5, () => {
        log(...p.lessons[0]);
        emit("learn");
      }],
      [19.6, () => {
        log(...p.lessons[1]);
        bumpVersion();
        setJourney(4, true);
        setState("BRAIN UPDATED", `PERSONA V${brain.version} · READY FOR THE NEXT ONE`);
        emit("learn");
      }]
    ];

    script.forEach(([at, fn]) => later(at, fn));

    if (!reducedMotion) later(27, play);
  }

  $("replay")?.addEventListener("click", play);

  /*
   * The globe: a prospect network with Adam's brain at its core.
   */
  function initGlobe() {
    const canvas = $("globe");
    if (!canvas) return;

    const { THREE, palette, rand } = G;
    const stage = G.createStage(canvas, { fov: 42, z: 8.5, fit: 0.9 });
    if (!stage) return;

    const ORIGIN = new THREE.Vector3();
    const root = new THREE.Group();
    stage.scene.add(root);

    root.add(G.wireframe(new THREE.IcosahedronGeometry(2.15, 4), 0x718cff, 0.16));

    const innerGlobe = G.wireframe(new THREE.IcosahedronGeometry(1.65, 3), 0x62f4e5, 0.06);
    root.add(innerGlobe);

    /*
     * Particle shell around the globe.
     */
    const shell = G.glowPoints(stage, 1500);
    const shellColor = new THREE.Color();
    const v = new THREE.Vector3();

    for (let i = 0; i < shell.count; i++) {
      shell.setPosition(i, G.randomOnSphere(rand(1.8, 2.75), v));
      shell.setColor(i, shellColor.copy(palette.blue).lerp(palette.cyan, Math.random()));
      shell.size[i] = rand(0.02, 0.05);
      shell.alpha[i] = rand(0.25, 0.8);
    }

    shell.flush();
    root.add(shell.points);

    /*
     * Orbital rings.
     */
    const rings = [];

    for (let i = 0; i < 3; i++) {
      const ring = G.circle(1, i === 1 ? 0x62f4e5 : 0x8296ff, 0.26 - i * 0.05);
      ring.scale.set(2.5 + i * 0.17, 1.2 + i * 0.18, 1);
      ring.rotation.set(0.65 + i * 0.47, 0.3 + i * 0.7, i * 0.3);
      root.add(ring);
      rings.push(ring);
    }

    /*
     * Prospects: connected nodes on the globe surface.
     */
    const nodes = G.fibonacciSphere(30, 2.25);
    const nodePoints = G.glowPoints(stage, nodes.length);
    const nodeFlash = new Float32Array(nodes.length);
    const nodeFlashColor = nodes.map(() => palette.cyan);

    nodes.forEach((node, i) => {
      nodePoints.setPosition(i, node);
      nodePoints.size[i] = rand(0.08, 0.13);
    });

    root.add(nodePoints.points);

    const connections = G.glowLines(nodes.length * 3);
    const edges = [];

    nodes.forEach((node, i) => {
      nodes
        .map((other, j) => ({ j, d: node.distanceTo(other) }))
        .filter(item => item.j !== i)
        .sort((a, b) => a.d - b.d)
        .slice(0, 3)
        .forEach(item => {
          if (item.j > i) edges.push([i, item.j]);
        });
    });

    edges.forEach(([a, b], i) => connections.setSegment(i, nodes[a], nodes[b], palette.cyan, 0.22));
    connections.flush(edges.length);
    root.add(connections.lines);

    /*
     * Traffic along the network.
     */
    const traffic = G.glowPoints(stage, 40);
    const trafficPaths = [];

    for (let i = 0; i < traffic.count; i++) {
      trafficPaths.push({
        edge: edges[i % edges.length],
        progress: Math.random(),
        speed: rand(0.12, 0.3)
      });
      traffic.size[i] = 0.06;
      traffic.alpha[i] = 0.8;
      traffic.setColor(i, palette.white);
    }

    root.add(traffic.points);

    /*
     * Adam's brain sits at the centre of the network.
     */
    const coreGroup = new THREE.Group();
    root.add(coreGroup);

    const core = G.glowPoints(stage, 460);
    const coreColor = new THREE.Color();
    const coreBaseSize = new Float32Array(core.count);

    for (let i = 0; i < core.count; i++) {
      core.setPosition(i, G.randomInSphere(0.72, v));
      core.setColor(i, coreColor.copy(palette.violet).lerp(palette.cyan, Math.random()));
      coreBaseSize[i] = rand(0.03, 0.07);
      core.alpha[i] = rand(0.4, 0.95);
    }

    core.flush();
    coreGroup.add(core.points);

    const halo = G.glowPoints(stage, 1);
    halo.size[0] = 2.6;
    halo.alpha[0] = 0.16;
    halo.setColor(0, palette.violet);
    halo.flush();
    coreGroup.add(halo.points);

    /*
     * The live link between the brain and the current prospect.
     */
    const link = G.glowLines(1);
    root.add(link.lines);

    /*
     * Particles that carry information between the brain and the world.
     */
    const transfers = G.glowPoints(stage, 120);
    const pool = Array.from({ length: transfers.count }, () => ({
      active: false,
      from: new THREE.Vector3(),
      control: new THREE.Vector3(),
      to: new THREE.Vector3(),
      t: 0,
      delay: 0,
      speed: 1,
      color: palette.cyan
    }));
    let poolCursor = 0;

    function transfer(from, to, color, count, { spread = 0.6, speed = 0.9, stagger = 0.08 } = {}) {
      for (let k = 0; k < count; k++) {
        const item = pool[poolCursor];
        poolCursor = (poolCursor + 1) % pool.length;

        item.active = true;
        item.from.copy(from);
        item.to.copy(to);
        item.control.copy(from).lerp(to, 0.5).add(G.randomInSphere(spread, v));
        item.t = 0;
        item.delay = k * stagger;
        item.speed = speed * rand(0.85, 1.15);
        item.color = color;
      }
    }

    let lock = -1;
    let linkLevel = 0;
    let coreBoost = 0;
    let spin = 0;
    let spinSpeed = 0.1;
    let spinTarget = 0.1;

    const world = new THREE.Vector3();
    const lockEl = document.querySelector(".target-lock");

    listeners.push(type => {
      if (type === "signal") {
        let s = Math.floor(Math.random() * nodes.length);
        if (s === lock) s = (s + 1) % nodes.length;
        nodeFlash[s] = 1.6;
        nodeFlashColor[s] = palette.amber;
        transfer(nodes[s], ORIGIN, palette.amber, 9);
      }

      if (type === "target") {
        root.updateMatrixWorld();
        let best = -Infinity;

        nodes.forEach((node, i) => {
          world.copy(node).applyMatrix4(root.matrixWorld);
          const facing = world.z - Math.abs(world.y) * 0.6 - Math.max(0, world.x) * 0.3;
          if (facing > best) {
            best = facing;
            lock = i;
          }
        });

        nodeFlash[lock] = 1.5;
        nodeFlashColor[lock] = palette.cyan;
        spinTarget = 0.015;
      }

      if (type === "send" && lock >= 0) {
        transfer(ORIGIN, nodes[lock], palette.cyan, 6, { spread: 0.3 });
      }

      if (type === "reply" && lock >= 0) {
        transfer(nodes[lock], ORIGIN, palette.white, 6, { spread: 0.3 });
      }

      if (type === "learn") {
        for (let k = 0; k < 16; k++) {
          transfer(G.randomOnSphere(rand(2.4, 2.9), world).clone(), ORIGIN, palette.violet, 1, { spread: 0.8, speed: 0.7 });
          pool[(poolCursor + pool.length - 1) % pool.length].delay = k * 0.04;
        }
      }

      if (type === "booked" && lock >= 0) {
        nodeFlash[lock] = 2;
        nodeFlashColor[lock] = palette.amber;
        coreBoost = 1.4;
        transfer(nodes[lock], ORIGIN, palette.amber, 14, { spread: 0.5 });
      }

      if (type === "reset") {
        lock = -1;
        spinTarget = 0.1;
      }
    });

    const temp = new THREE.Vector3();

    stage.start((dt, time) => {
      const growth = Math.min(1, 0.12 + G.ease(Math.min(1, time / 3.2)) * 0.88);
      root.scale.setScalar(growth);

      spinSpeed += (spinTarget - spinSpeed) * Math.min(1, dt * 1.5);
      spin += spinSpeed * dt;

      root.rotation.y = spin + stage.pointer.x * 0.25;
      root.rotation.x = Math.sin(time * 0.17) * 0.09 + stage.pointer.y * 0.15;
      root.position.y = Math.sin(time * 0.5) * 0.05;

      innerGlobe.rotation.y = time * 0.11;
      innerGlobe.rotation.x = -time * 0.04;

      rings.forEach((ring, i) => {
        ring.rotation.z = Math.sin(time * 0.16 + i) * 0.22;
        ring.rotation.y += (i % 2 ? 0.09 : -0.09) * dt;
      });

      // Network traffic
      trafficPaths.forEach((path, i) => {
        path.progress = (path.progress + path.speed * dt) % 1;
        temp.copy(nodes[path.edge[0]]).lerp(nodes[path.edge[1]], path.progress);
        traffic.setPosition(i, temp);
      });
      traffic.flush();

      // Prospect nodes
      for (let i = 0; i < nodes.length; i++) {
        nodeFlash[i] = Math.max(i === lock ? 0.7 : 0, nodeFlash[i] - dt * 0.7);
        const f = nodeFlash[i];
        nodePoints.setColor(i, shellColor.copy(palette.cyan).lerp(nodeFlashColor[i], Math.min(1, f)));
        nodePoints.size[i] = 0.1 * (1 + f * 1.8);
        nodePoints.alpha[i] = 0.75 + Math.min(1, f) * 0.25;
      }
      nodePoints.flush();

      // Brain core
      coreBoost = Math.max(0, coreBoost - dt * 0.9);
      coreGroup.rotation.y = time * 0.35;
      coreGroup.rotation.z = time * 0.12;
      coreGroup.scale.setScalar(1 + Math.sin(time * 2) * 0.03 + coreBoost * 0.12);

      for (let i = 0; i < core.count; i++) {
        core.size[i] = coreBaseSize[i] * (1 + coreBoost * 0.9 + Math.sin(time * 3 + i) * 0.15);
      }
      core.flush();

      halo.alpha[0] = 0.14 + coreBoost * 0.2;
      halo.flush();

      // Link to the current prospect
      linkLevel += ((lock >= 0 ? 1 : 0) - linkLevel) * Math.min(1, dt * 2);
      if (lock >= 0) {
        link.setSegment(0, ORIGIN, nodes[lock], palette.violet, 0.6 * linkLevel, palette.cyan, 0.9 * linkLevel);
      } else {
        link.setSegment(0, ORIGIN, ORIGIN, palette.cyan, 0);
      }
      link.flush();

      // Information transfers
      for (let i = 0; i < pool.length; i++) {
        const item = pool[i];

        if (!item.active) {
          transfers.alpha[i] = 0;
          continue;
        }

        if (item.delay > 0) {
          item.delay -= dt;
          transfers.alpha[i] = 0;
          continue;
        }

        item.t += dt * item.speed;

        if (item.t >= 1) {
          item.active = false;
          transfers.alpha[i] = 0;
          if (item.to.lengthSq() < 0.01) coreBoost = Math.min(1.6, coreBoost + 0.12);
          continue;
        }

        G.bezier(temp, item.from, item.control, item.to, G.ease(item.t));
        transfers.setPosition(i, temp);
        transfers.setColor(i, item.color);
        transfers.size[i] = 0.09;
        transfers.alpha[i] = Math.sin(item.t * Math.PI) * 0.95 + 0.05;
      }
      transfers.flush();

      // Keep the DOM target lock on the selected prospect
      if (lock >= 0 && lockEl) {
        root.updateMatrixWorld();
        temp.copy(nodes[lock]).applyMatrix4(root.matrixWorld).project(stage.camera);
        lockEl.style.setProperty("--x", ((temp.x * 0.5 + 0.5) * stage.width).toFixed(1) + "px");
        lockEl.style.setProperty("--y", ((-temp.y * 0.5 + 0.5) * stage.height).toFixed(1) + "px");
      }
    });
  }

  renderTraits();
  initGlobe();
  engine.classList.add("scanning");
  play();
}
